import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { CreateNoteDto, NoteCategory, NotePriority } from '../../api/notes';
import { BrandModal, BrandModalBody, BrandModalFooter, BrandModalHeader } from '../ui/BrandModal';

interface CreateNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateNoteDto) => Promise<void>;
}

const CreateNoteModal: React.FC<CreateNoteModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState<CreateNoteDto>({
    title: '',
    content: '',
    category: NoteCategory.GENERAL,
    priority: NotePriority.MEDIUM,
    birdId: '',
    speciesId: '',
    tags: [],
    isPinned: false,
    isArchived: false,
    reminderDate: '',
  });

  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = {
        ...formData,
        birdId: formData.birdId || undefined,
        speciesId: formData.speciesId || undefined,
        reminderDate: formData.reminderDate || undefined,
      };

      await onSubmit(submitData);
      
      // Reset form
      setFormData({
        title: '',
        content: '',
        category: NoteCategory.GENERAL,
        priority: NotePriority.MEDIUM,
        birdId: '',
        speciesId: '',
        tags: [],
        isPinned: false,
        isArchived: false,
        reminderDate: '',
      });
      setTagInput('');
    } catch (error) {
      console.error('Error creating note:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags?.filter(tag => tag !== tagToRemove) || []
    }));
  };

  const handleTagInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      content: '',
      category: NoteCategory.GENERAL,
      priority: NotePriority.MEDIUM,
      birdId: '',
      speciesId: '',
      tags: [],
      isPinned: false,
      isArchived: false,
      reminderDate: '',
    });
    setTagInput('');
    onClose();
  };

  return (
    <BrandModal open={isOpen} onClose={handleClose} maxWidthClass="max-w-2xl">
      <BrandModalHeader title="Create Note" onClose={handleClose} />
      <form id="create-note-form" onSubmit={handleSubmit}>
        <BrandModalBody>
              <div className="space-y-4">
                {/* Title */}
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-slate-700">
                    Title *
                  </label>
                  <input
                    type="text"
                    id="title"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                    placeholder="Enter note title"
                  />
                </div>

                {/* Content */}
                <div>
                  <label htmlFor="content" className="block text-sm font-medium text-slate-700">
                    Content *
                  </label>
                  <textarea
                    id="content"
                    required
                    rows={6}
                    value={formData.content}
                    onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                    placeholder="Enter note content"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Category */}
                  <div>
                    <label htmlFor="category" className="block text-sm font-medium text-slate-700">
                      Category
                    </label>
                    <select
                      id="category"
                      value={formData.category}
                      onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value as NoteCategory }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                    >
                      {Object.values(NoteCategory).map(category => (
                        <option key={category} value={category}>
                          {category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' ')}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Priority */}
                  <div>
                    <label htmlFor="priority" className="block text-sm font-medium text-slate-700">
                      Priority
                    </label>
                    <select
                      id="priority"
                      value={formData.priority}
                      onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value as NotePriority }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                    >
                      {Object.values(NotePriority).map(priority => (
                        <option key={priority} value={priority}>
                          {priority.charAt(0).toUpperCase() + priority.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Bird ID */}
                  <div>
                    <label htmlFor="birdId" className="block text-sm font-medium text-slate-700">
                      Bird ID (Optional)
                    </label>
                    <input
                      type="text"
                      id="birdId"
                      value={formData.birdId}
                      onChange={(e) => setFormData(prev => ({ ...prev, birdId: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      placeholder="Associate with bird"
                    />
                  </div>

                  {/* Species ID */}
                  <div>
                    <label htmlFor="speciesId" className="block text-sm font-medium text-slate-700">
                      Species ID (Optional)
                    </label>
                    <input
                      type="text"
                      id="speciesId"
                      value={formData.speciesId}
                      onChange={(e) => setFormData(prev => ({ ...prev, speciesId: e.target.value }))}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      placeholder="Associate with species"
                    />
                  </div>
                </div>

                {/* Reminder Date */}
                <div>
                  <label htmlFor="reminderDate" className="block text-sm font-medium text-slate-700">
                    Reminder Date (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    id="reminderDate"
                    value={formData.reminderDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, reminderDate: e.target.value }))}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                  />
                </div>

                {/* Tags */}
                <div>
                  <label htmlFor="tags" className="block text-sm font-medium text-slate-700">
                    Tags
                  </label>
                  <div className="mt-1 flex rounded-md shadow-sm">
                    <input
                      type="text"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={handleTagInputKeyPress}
                      className="flex-1 min-w-0 block w-full border-gray-300 rounded-none rounded-l-md focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm"
                      placeholder="Add tag and press Enter"
                    />
                    <button
                      type="button"
                      onClick={handleAddTag}
                      className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-slate-50/80 text-slate-500 text-sm"
                    >
                      Add
                    </button>
                  </div>
                  {formData.tags && formData.tags.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {formData.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-100 text-cyan-800"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => handleRemoveTag(tag)}
                            className="flex-shrink-0 ml-1 h-4 w-4 rounded-full inline-flex items-center justify-center text-indigo-400 hover:bg-indigo-200 hover:text-cyan-500 focus:outline-none focus:bg-cyan-500 focus:text-white"
                          >
                            <XMarkIcon className="h-2 w-2" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* Options */}
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      id="isPinned"
                      type="checkbox"
                      checked={formData.isPinned}
                      onChange={(e) => setFormData(prev => ({ ...prev, isPinned: e.target.checked }))}
                      className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isPinned" className="ml-2 block text-sm text-slate-900">
                      Pin this note
                    </label>
                  </div>
                </div>
              </div>
        </BrandModalBody>

        <BrandModalFooter>
          <button type="button" onClick={handleClose} className="btn btn-secondary text-sm">
            Cancel
          </button>
          <button
            type="submit"
            form="create-note-form"
            disabled={loading || !formData.title.trim() || !formData.content.trim()}
            className="btn btn-primary text-sm disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Note'}
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
};

export default CreateNoteModal; 